// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  ICertificationWebpageCancelAttrs,
  ICertificationWebpageClaimAttrs,
  ICertificationWebpageDownloadDocumentAttrs,
  ICertificationWebpageGetAttrs,
  ICertificationWebpageGetDetailAttrs,
} from 'interfaces/webpage/certification/certification-view.interface'

// Utils
import { assessmentUtils_isEditable } from 'utils/assessment/assessment.utils'

const useCertification = () => {
  // Hook
  const {
    certificationWebpage: {
      certificationWebpage_loading,
      certificationWebpage_webpage,
      certificationWebpage_detail,
      certificationWebpage_CLEAR_CERTIFICATE_WEBPAGE,
      certificationWebpage_CLEAR_DETAIL,
      certificationWebpage_fetchCertificationWebpage: fetchCertificationWebpage,
      certificationWebpage_fetchDetail: fetchDetail,
      certificationWebpage_claim: claim,
      certificationWebpage_cancel: cancel,
      certificationWebpage_downloadDocument: downloadDocument,
    },
  } = useStores()

  /**
   * @description Check if certification editable
   *
   * @param {boolean} isFirstTime
   *
   * @return {boolean} boolean
   */
  const certificationWebpage_isEditablePhase = useCallback(
    (isFirstTime?: boolean): boolean => {
      if (certificationWebpage_detail.currentCertification !== null)
        return assessmentUtils_isEditable(
          certificationWebpage_detail.currentCertification.phase!,
          certificationWebpage_detail.currentCertification.status!,
          { isFirstTime },
        )
      else return true
    },
    [certificationWebpage_detail],
  )

  /**
   * @description Fetch certification webpage
   *
   * @param {ICertificationWebpageGetAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpage_fetchCertificationWebpage = useCallback(
    (payload: ICertificationWebpageGetAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchCertificationWebpage({
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchCertificationWebpage],
  )

  /**
   * @description Fetch certification detail
   *
   * @param {ICertificationWebpageGetDetailAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpage_fetchDetail = useCallback(
    (payload: ICertificationWebpageGetDetailAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchDetail],
  )

  /**
   * @description Claim certification
   *
   * @param {ICertificationWebpageClaimAttrs} payload
   *
   * @return {Promise<number>} Promise<number>
   */
  const certificationWebpage_claim = useCallback(
    (payload: ICertificationWebpageClaimAttrs): Promise<number> => {
      return new Promise((resolve, reject) => {
        claim({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: assessmentId => resolve(assessmentId),
          onError: reject,
        })
      })
    },
    [claim],
  )

  /**
   * @description Cancel certification
   *
   * @param {ICertificationWebpageCancelAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpage_cancel = useCallback(
    (payload: ICertificationWebpageCancelAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        cancel({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [cancel],
  )

  /**
   * @description Download document
   *
   * @param {ICertificationWebpageDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpage_downloadDocument = useCallback(
    (payload: ICertificationWebpageDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [downloadDocument],
  )

  return {
    certificationWebpage_loading,
    certificationWebpage_webpage,
    certificationWebpage_detail,
    certificationWebpage_CLEAR_CERTIFICATE_WEBPAGE,
    certificationWebpage_CLEAR_DETAIL,
    certificationWebpage_fetchCertificationWebpage,
    certificationWebpage_fetchDetail,
    certificationWebpage_claim,
    certificationWebpage_cancel,
    certificationWebpage_downloadDocument,
    certificationWebpage_isEditablePhase,
  }
}

export { useCertification }
